import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import {
  ActionActivator,
  Button,
  Container,
  EmptyAction,
  FunctionAction,
  InterpolateCopyAction,
  lerp,
  SceneObject,
  SequenceAction,
  TextSceneObject,
} from '@cgs/wge';
import { EndFreeSpinsPopupController } from './end-free-spins-popup.controller';
import { BaseSlotPopupView, SlotPopups } from '../../../common/slot/views/base_popup_view';
import {
  END_FREE_SPINS_DEFAULT_PARAMS,
  EndFreeSpinsPopupParams,
} from './end-free-spins-popup.typings';

const DEFAULT_COUNTUP_DURATION = 4.0;

export class EndFreeSpinsPopupView extends BaseSlotPopupView<EndFreeSpinsPopupController> {
  protected readonly popupView: SceneObject;

  private readonly _totalWins: TextSceneObject[];
  private readonly _totalLose: TextSceneObject;
  private readonly _spinsCounts: TextSceneObject[];
  private readonly _spinsLoseCounts: TextSceneObject[];
  private readonly _pages: SceneObject[];
  private readonly _actionActivator: ActionActivator;
  private readonly numberFormatter: INumberFormatter;
  protected readonly params: EndFreeSpinsPopupParams;

  private _hasWin: boolean;
  private _allreadyClosed: boolean = false;
  private _countupActivator: ActionActivator | null = null;
  private _pendingCountupValue: number | null = null;

  constructor(
    protected readonly container: Container,
    _root: SceneObject,
    popupView: SceneObject,
    params: Partial<EndFreeSpinsPopupParams> = {}
  ) {
    super(_root, popupView, null, SlotPopups.EndFreeSpins);
    this.popupView = popupView;
    this.params = { ...END_FREE_SPINS_DEFAULT_PARAMS, ...params };

    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);

    const closeButtons = this.popupView.findAllById<Button>(this.params.closeButtonsName!);
    closeButtons.forEach((closeButton) =>
      closeButton.clicked.listen(() => this.onBackToGameClicked())
    );

    this._totalWins = this.popupView.findAllById<TextSceneObject>(this.params.totalWinTextId!);
    this._totalLose = this.popupView.findById<TextSceneObject>(this.params.totalLoseTextId!)!;
    this._spinsCounts = this.popupView.findAllById<TextSceneObject>(this.params.spinsWinTextId!);
    this._spinsLoseCounts = this.popupView.findAllById<TextSceneObject>(
      this.params.spinsLoseTextId!
    );

    this._pages = this.popupView.findAllById(this.params.animSceneId!);

    if (this.params.closeOnAnimFinished) {
      const winStateName = this.params.winCloseStateName!;
      const loseStateName = this.params.loseCloseStateName!;
      for (const _page of this._pages) {
        const winState = _page.stateMachine!.findById(winStateName);
        const loseState = _page.stateMachine!.findById(loseStateName);

        if (_page && _page.stateMachine && winState && loseState) {
          winState.enterAction.done.listen(() => this.controller.onCloseClicked());
          loseState.enterAction.done.listen(() => this.controller.onCloseClicked());
        }
      }
    }

    this._actionActivator = new ActionActivator(this.view);
    this._actionActivator.action = new SequenceAction([
      new EmptyAction().withDuration(this.params.autoCloseTimeout!),
      new FunctionAction(() => {
        this.onBackToGameClicked();
        this._actionActivator.end();
      }),
    ]);
  }

  protected onBackToGameClicked(): void {
    if (this._allreadyClosed) {
      return;
    }
    this._allreadyClosed = true;
    this._stopCountupAnim();

    const closeStateName = this._hasWin
      ? this.params.winCloseStateName!
      : this.params.loseCloseStateName!;

    const closablePages = this._pages.filter((_page) => _page.stateMachine?.findById(closeStateName));

    if (closablePages.length > 0) {
      const sub = closablePages[0].stateMachine!.findById(closeStateName)!.enterAction.done.listen(() => {
        sub.cancel();
        this.controller.onCloseClicked();
      });
      for (const _page of closablePages) {
        _page.stateMachine!.switchToState(closeStateName);
      }
    } else {
      this.controller.onCloseClicked();
    }
  }

  public startActivator(): void {
    this._actionActivator.start();
  }

  public setSpins(spins: number): void {
    this._allreadyClosed = false;
    for (const _spinsCount of this._spinsCounts) {
      if (_spinsCount) {
        _spinsCount.text = spins.toString();
      }
    }
    for (const _spinsLoseCount of this._spinsLoseCounts) {
      if (_spinsLoseCount) {
        _spinsLoseCount.text = spins.toString();
      }
    }
  }

  public onShown(): void {
    this.controller.onEndFreeSpinShown();
    if (this._pendingCountupValue !== null) {
      this._startCountupAnim(this._pendingCountupValue);
      this._pendingCountupValue = null;
    }
  }

  public setMode(hasWin: boolean): void {
    this._hasWin = hasWin;

    for (const _page of this._pages) {
      if (_page && _page.stateMachine) {
        const state = hasWin ? this.params.winStateName! : this.params.loseStateName!;
        if (!hasWin) {
          console.log('end free spin: no win');
        } else {
          console.log('end free spin: congratulation');
        }

        _page.stateMachine.switchToState(state);
      }
    }
  }

  public setTotalWin(totalWin: number): void {
    if (this.params.animateTotalWin) {
      // Откладываем старт до onShown(): onEndFreeSpinShown вызывается дважды
      // (из onStateEntered до show() и из onShown), поэтому здесь только
      // сохраняем значение — анимацию запускаем один раз, когда view в сцене.
      this._pendingCountupValue = totalWin;
      return;
    }
    this._setTotalWinText(totalWin);
  }

  private _startCountupAnim(value: number): void {
    this._stopCountupAnim();
    this._setTotalWinText(0);
    const delay = this.params.animateTotalWinDelay ?? 0;
    const duration = this.params.animateTotalWinDuration ?? DEFAULT_COUNTUP_DURATION;
    const countupAction = new InterpolateCopyAction<number>()
      .withInterpolateFunction(lerp)
      .withDuration(duration)
      .withValues(0, value);
    countupAction.valueChange.listen((current: number) => this._setTotalWinText(current));
    this._countupActivator = new ActionActivator(this.view);
    this._countupActivator.action = new SequenceAction([
      new EmptyAction().withDuration(delay),
      new FunctionAction(() => this._onCountupStarted()),
      countupAction,
      new FunctionAction(() => {
        this._setTotalWinText(value);
        this._onCountupFinished();
      }),
    ]);
    this._countupActivator.start();
  }

  private _stopCountupAnim(): void {
    if (this._countupActivator) {
      this._countupActivator.end();
      this._countupActivator = null;
    }
  }

  private _setTotalWinText(value: number): void {
    const text = this.params.isMoneyResult
      ? this.numberFormatter.formatMoney(value)
      : this.numberFormatter.format(value);
    this._totalWins.forEach((t) => (t.text = text));
    if (this._totalLose) {
      this._totalLose.text = text;
    }
  }

  protected _onCountupStarted(): void {}

  protected _onCountupFinished(): void {}

  public setFreeSpinsMode(modePickerId: string, mode: string): void {
    for (const _page of this._pages) {
      const modePickers = _page.findAllById(modePickerId);

      for (const modePicker of modePickers) {
        if (modePicker.stateMachine) {
          modePicker.stateMachine.switchToState(mode);
        }
      }
    }
  }
}
